// La conversación entera: lista de mensajes + barra de entrada.
//
// ES EL COMPONENTE COMPARTIDO. El panel lateral y la pantalla dedicada montan este mismo
// árbol y solo le cambian el ancho de la columna y los márgenes.
//
// Las burbujas y las TARJETAS son las del asistente (`MessageRow` de `ChatPanelV2`, que
// dibuja el texto, las propuestas de generación, los pickers, la galería, el soporte y el
// "sin créditos") — ADR-2026-09-08-el-chat-toma-el-motor-del-asistente. Debajo de cada
// respuesta van los extras del Chat: la tarjeta de un gráfico y los chips de sugerencia.
//
// Y arriba del input está el SLOT DE LA CARD ACTIVA, igual que en el asistente. No es
// decoración: `MessageRow` sólo dibuja las cards resueltas y las inline-pendientes, así
// que una card que necesita el turno vivo —`pick_product`, `confirm_research`, las de
// lanzamientos— no se dibujaba en NINGÚN lado. El turno se quedaba esperando una respuesta
// que el usuario no tenía cómo dar, sin siquiera el "pensando" (que se apaga justamente
// cuando hay card activa): el chat congelado (ADR-2026-09-09-el-chat-necesita-su-slot-de-card).

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { useAvisoGenerando, PuntoPulsante } from '../../components/AvisoGenerandoDeFondo';
import { MessageRow, ToolCardRenderer } from '../../pages/v2/ChatPanelV2';
import { useRegistrarPantallaDeCarga } from '../../pages/v2/useGeneracionesEnCurso';
import { isInlinePendingCard } from '../../pages/v2/restaurarToolCards';
import { esperaAlUsuario } from '../../pages/v2/actividadDelAgente';
import { useChatAtlas } from '../ChatAtlasContext';
import { useEntornoChatAtlas } from '../entornoContext';
import { hayGeneracionEnVuelo } from '../lienzo/tandasDelLienzo';
import { BarraEntradaChat } from './BarraEntradaChat';
import { ExtrasBurbuja, type VistaGrafico } from './BurbujaMensaje';
import { InicioAgente } from './InicioAgente';
import { MascotaAgente } from './MascotaAgente';
import { ModalDeFuentes } from './ModalDeFuentes';
import { SelectoresDeAdjuntos, type SelectorAbierto } from './SelectoresDeAdjuntos';
import { pestanaDeAccion, type PestanaBiblioteca } from './bibliotecaDeAdjuntos';
import { sugerenciasDelTurno } from './sugerenciasDelTurno';
import { useEsEscritorio } from './useEsEscritorio';
import { usePrimeraVezEnElAgente } from './usePrimeraVezEnElAgente';
import { useScrollDeConversacion } from './useScrollDeConversacion';

interface Props {
  /** Ancho de la columna de mensajes. La pantalla dedicada centra en una columna
   *  cómoda; el panel usa todo el ancho de la franja. */
  anchoColumna?: string;
  /** Padding lateral. */
  padding?: string;
  /** Qué hace la tarjeta de una respuesta gráfica. En el panel lateral no hay ancho
   *  para dibujarla, así que manda a la pantalla dedicada. */
  vistaGrafico?: VistaGrafico;
  /** Se llama con el id del gráfico al tocar su tarjeta. */
  onAbrirGrafico?: (id: string) => void;
  /** Panel lateral (angosto) o pantalla dedicada: cambia los tamaños del arranque y el
   *  alto del composer, no las piezas (ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2). */
  variante?: 'panel' | 'pantalla';
  /** En la pantalla dedicada de desktop, las cards del "+" que eligen del catálogo abren
   *  la BIBLIOTECA (la columna de la derecha) en su pestaña, en vez del selector modal
   *  (ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-del-agente). Sin esto, el
   *  selector de siempre. */
  onAbrirBiblioteca?: (pestana: PestanaBiblioteca) => void;
}

export const ConversacionChatAtlas: React.FC<Props> = ({
  anchoColumna = 'max-w-3xl',
  padding = 'px-4 sm:px-6',
  vistaGrafico = 'ir-a-pantalla',
  onAbrirGrafico,
  variante = 'pantalla',
  onAbrirBiblioteca,
}) => {
  const chat = useChatAtlas();
  const entorno = useEntornoChatAtlas();
  const { companyId } = entorno;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const esEscritorio = useEsEscritorio();
  // Selector del catálogo abierto (producto / modelo / pose / fondo / imagen ya
  // generada) — ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca.
  const [selector, setSelector] = useState<SelectorAbierto>(null);
  const abrirSelector = (accion: NonNullable<SelectorAbierto>) => {
    const pestana = pestanaDeAccion(accion);
    if (onAbrirBiblioteca && pestana) onAbrirBiblioteca(pestana);
    else setSelector(accion);
  };
  // El "+" abre el selector de fuentes con pestañas, el MISMO de la caja de la home, y en
  // el teléfono su hoja (ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes).
  const [fuentes, setFuentes] = useState(false);
  // ¿Está escribiendo? En el arranque de mobile, el foco en la caja esconde el título y
  // muestra las sugerencias arriba de la caja. El blur se aplica con un respiro: tocar
  // una sugerencia primero le saca el foco al textarea y recién después dispara el
  // click — si las sugerencias se fueran en el acto, el click caería en la nada.
  const [escribiendo, setEscribiendo] = useState(false);
  const blurRef = useRef<number | null>(null);
  const alEnfocar = (enfocado: boolean) => {
    if (blurRef.current) {
      window.clearTimeout(blurRef.current);
      blurRef.current = null;
    }
    if (enfocado) setEscribiendo(true);
    else blurRef.current = window.setTimeout(() => setEscribiendo(false), 250);
  };
  useEffect(() => () => {
    if (blurRef.current) window.clearTimeout(blurRef.current);
  }, []);
  // Una sugerencia del arranque COMPLETA la caja y deja el cursor ahí; no manda: el
  // usuario retoca el pedido (su producto, la red, la duración) antes de enviarlo
  // (ADR-2026-09-12-las-sugerencias-completan-la-caja). El contador le pide el foco al
  // textarea; es un número para que la misma sugerencia dos veces también enfoque.
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0);
  const completarConSugerencia = (texto: string) => {
    chat.setBorrador(texto);
    setPedidoDeFoco((n) => n + 1);
  };

  // El scroll (ADR-2026-09-13-el-scroll-del-agente): vuelve a donde quedó ESTE chat, sigue
  // la respuesta mientras llega, se detiene si el usuario sube, y ofrece el botón de "ir al
  // final". La clave es el id del chat; un chat nuevo todavía sin id comparte un marcador
  // (está en el fondo igual).
  const { lejosDelFondo, irAlFondo, alEnviar } = useScrollDeConversacion({
    scrollRef,
    clave: chat.motor.getChatId() ?? 'sin-chat',
    activo: chat.mensajes.length > 0,
  });

  const ultimo = chat.mensajes[chat.mensajes.length - 1];
  /**
   * ¿El agente está trabajando, o me está esperando a mí?
   *
   * No alcanza con "hay una card pendiente": el lote abierto queda pendiente MIENTRAS el
   * agente le escribe los prompts del set siguiente, que es la parte más larga del turno
   * (ADR-2026-09-14-el-lote-abierto-no-es-una-card-que-espera). Con la regla vieja la
   * mascota se congelaba y la etiqueta —que el motor venía calculando bien, "Escribiendo
   * el prompt 7…"— no se dibujaba, así que el chat parecía colgado.
   */
  const trabajando = chat.streaming && !esperaAlUsuario(chat.motor.activeToolCard);
  // "Pensando…": está trabajando y todavía no hay nada visible (ni texto ni tarjeta).
  const pensando =
    trabajando &&
    (!ultimo ||
      ultimo.role === 'user' ||
      (!ultimo.text && (!ultimo.toolCards || ultimo.toolCards.length === 0)));
  const vacio = chat.mensajes.length === 0;
  // EL AVISO DE "GENERANDO DE FONDO" NO SE MONTA SOBRE EL CHAT
  // (ADR-2026-10-05-el-aviso-de-generando-no-se-monta-sobre-el-chat). Con mensajes, el
  // composer vive en la esquina de abajo a la derecha, justo donde flota el aviso: la
  // conversación se registra como pantalla que muestra la carga —lo que genera el chat
  // ya se ve en sus cards— y lo que se genera en OTRO lado se cuenta en una línea arriba
  // del composer, en vez de flotar encima. Con el chat vacío el composer está en el medio
  // y el aviso de la esquina no tapa nada.
  useRegistrarPantallaDeCarga(!vacio);
  const generandoEnElChat = useMemo(() => hayGeneracionEnVuelo(chat.mensajes), [chat.mensajes]);
  const avisoDeFondo = useAvisoGenerando({ ignorarPantallas: true });
  const lineaDeFondo = !vacio && !generandoEnElChat ? avisoDeFondo : null;
  // Las sugerencias del turno, siempre debajo del ÚLTIMO mensaje del asistente
  // (ADR-2026-10-05-las-sugerencias-van-al-final-del-turno).
  const sugerencias = useMemo(() => sugerenciasDelTurno(chat.mensajes), [chat.mensajes]);
  // Primera vez en el Agente: en vez del arranque va la pantalla de bienvenida, hasta que
  // toque "Genial, empecemos" (ADR-2026-09-14-la-bienvenida-del-agente). Sólo se pregunta
  // con la conversación vacía, que es cuando ese arranque existe.
  // El `usuarioId` va por parámetro: la marca de "ya la cerró" es POR CUENTA
  // (ADR-2026-09-15-la-bienvenida-se-acuerda-por-cuenta).
  const bienvenida = usePrimeraVezEnElAgente(vacio, entorno.usuarioId);
  // Lo que va al slot: la card activa que NO se dibuja inline. Las inline-pendientes
  // (propuestas de generación, galerías, planilla/PDF) ya viven en el stream y ponerlas
  // acá las mostraría dos veces — el mismo reparto que hace `AsistenteMode`.
  const cardActiva =
    chat.motor.activeToolCard && !isInlinePendingCard(chat.motor.activeToolCard)
      ? chat.motor.activeToolCard
      : null;

  // El composer es UNO: con el chat vacío vive en el medio del arranque; con mensajes,
  // al pie. Se remonta al pasar de un lugar al otro (el borrador y los adjuntos viven en
  // el contexto, así que no se pierde nada).
  const composer = (
    <BarraEntradaChat
      valor={chat.borrador}
      onCambiarValor={chat.setBorrador}
      onEnviar={(texto) => {
        alEnviar();
        return chat.enviarMensaje(texto);
      }}
      streaming={chat.streaming}
      onDetener={chat.detener}
      cola={chat.cola}
      onEncolar={chat.encolarMensaje}
      onQuitarDeCola={chat.quitarDeCola}
      interruptores={chat.interruptores}
      onCambiarInterruptor={chat.setInterruptor}
      preferencias={chat.preferencias}
      onCambiarPreferencia={chat.setPreferencia}
      adjuntos={chat.adjuntos}
      onAgregarAdjunto={chat.agregarAdjunto}
      onQuitarAdjunto={chat.quitarAdjunto}
      adjuntosTentativos={chat.adjuntosTentativos}
      loteFotos={chat.loteFotos}
      onAdjuntarLote={chat.agregarLoteFotos}
      onQuitarLote={chat.quitarLoteFotos}
      onAbrirSelector={abrirSelector}
      companyId={companyId}
      inteligencia={chat.inteligencia}
      onCambiarInteligencia={chat.setInteligencia}
      puedeSubirInteligencia={chat.puedeSubirInteligencia}
      modeloDeImagen={chat.modeloDeImagen}
      onCambiarModeloDeImagen={chat.setModeloDeImagen}
      // En el arranque de mobile NO se enfoca sola: el título y la mascota se ven hasta
      // que el usuario toca la caja (y el teclado no salta apenas entra).
      autoFoco={esEscritorio || !vacio}
      alto={vacio && variante === 'pantalla' ? 'amplio' : 'normal'}
      // En desktop también el panel lateral y la conversación con el lienzo abierto, si
      // la fila tiene lugar (ADR-2026-09-25-agregar-con-rotulo-en-el-panel-y-con-el-lienzo).
      rotuloAgregar={variante === 'pantalla' ? true : esEscritorio ? 'si-entra' : false}
      // El "+" abre el selector de fuentes (no el menú) y la fila lleva la Inteligencia y
      // el engranaje de configuración, como la caja de la home
      // (ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes).
      onAbrirFuentes={() => setFuentes(true)}
      pastillasDeContenido
      variante={variante}
      onEnfoque={alEnfocar}
      pedidoDeFoco={pedidoDeFoco}
    />
  );

  if (vacio) {
    return (
      <>
        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${padding} py-6`}
          style={{ scrollbarWidth: 'thin' }}
        >
          <InicioAgente
            variante={variante}
            contexto={chat.contexto?.pantalla}
            muestraCosto={chat.inteligencia !== 'baja'}
            bloqueado={chat.streaming}
            onSugerencia={completarConSugerencia}
            anchoColumna={anchoColumna}
            escribiendo={escribiendo}
            bienvenida={bienvenida.mostrar}
            onEmpezar={bienvenida.empezar}
          >
            {composer}
          </InicioAgente>
        </div>
        <ModalDeFuentes abierto={fuentes} onCerrar={() => setFuentes(false)} />
        <SelectoresDeAdjuntos
          abierto={selector}
          onCerrar={() => setSelector(null)}
          onAdjuntar={chat.agregarAdjunto}
          productos={entorno.productos}
          modelos={entorno.modelos}
          usuarioId={entorno.usuarioId}
          companyId={companyId}
          onModeloCreado={entorno.refrescarModelos}
        />
      </>
    );
  }

  return (
    <>
      {/* El scroller va envuelto para que el botón de "ir al final" flote sobre él, no
          sobre el composer. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        // `overscroll-contain`: llegado al final, la rueda NO sigue scrolleando la
        // pantalla de atrás. Abajo hay más aire que arriba (`pb-20`): la última
        // respuesta y la mascota no quedan pegadas al composer.
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${padding} pt-6 pb-20`}
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className={`mx-auto flex flex-col gap-5 ${anchoColumna}`}>
          {chat.mensajes.map((m, i) => (
              <div key={i} className="flex flex-col gap-3">
                <MessageRow msg={m} chat={chat.motor} showPendingCards />
                {m.role === 'assistant' && (
                  <ExtrasBurbuja
                    mensaje={m}
                    sugerencias={sugerencias?.indice === i ? sugerencias.sugerencias : undefined}
                    bloqueado={chat.streaming}
                    onSugerencia={(texto) => chat.enviarMensaje(texto)}
                    vistaGrafico={vistaGrafico}
                    onAbrirGrafico={onAbrirGrafico}
                    graficoActivoId={chat.graficoActivoId}
                  />
                )}
              </div>
            ))}

          {/* Cumbre al pie de la conversación (ADR-2026-09-12-la-mascota-acompana-la-
              conversacion): quieta mientras espera; desde que el usuario manda un mensaje
              cruza y gira en la pista corta, con la etiqueta de lo que está haciendo
              debajo. Con una card activa esperando el click no hay nada que animar. */}
          <MascotaAgente
            modo={trabajando ? 'trabajando' : 'quieta'}
            etiqueta={chat.actividad ?? (pensando ? 'Pensando…' : null)}
          />
        </div>
      </div>

      {/* "Ir al final": aparece cuando el usuario subió. Redondo, blanco, con sombra, en
          el medio y sobre el borde de abajo del scroller. */}
      {lejosDelFondo && (
        <button
          type="button"
          onClick={irAlFondo}
          aria-label="Ir al final de la conversación"
          className="absolute bottom-4 left-1/2 z-[5] inline-flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-white text-[#02062B]/[0.7] shadow-[0_4px_16px_rgba(2,6,43,0.18)] ring-1 ring-black/[0.06] transition-colors hover:bg-neutral-50 hover:text-brand-black animate-fade-in"
        >
          <ArrowDown className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" />
        </button>
      )}
      </div>

      {lineaDeFondo && (
        <div className={`shrink-0 ${padding} pt-1`}>
          <p
            role="status"
            aria-live="polite"
            className={`mx-auto flex items-center gap-2 px-1 text-[12.5px] font-medium text-[#02062B]/[0.55] ${anchoColumna}`}
          >
            <PuntoPulsante className="text-brand-primary" />
            {lineaDeFondo}
          </p>
        </div>
      )}

      {cardActiva && (
        <div className={`shrink-0 ${padding} pt-2`}>
          <div
            key={cardActiva.id}
            className={`mx-auto ${anchoColumna} animate-mode-swap overflow-y-auto rounded-2xl bg-white shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04]`}
            style={{ maxHeight: '55dvh' }}
          >
            <ToolCardRenderer card={cardActiva} chat={chat.motor} embedded />
          </div>
        </div>
      )}

      <div
        className={`shrink-0 ${padding} pt-3`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      >
        <div className={`mx-auto ${anchoColumna}`}>{composer}</div>
      </div>

      {/* Los selectores reales de la app (portaleados): producto, modelo, pose, fondo e
          imagen ya generada. Sólo se montan cuando se abren. */}
      <ModalDeFuentes abierto={fuentes} onCerrar={() => setFuentes(false)} />
      <SelectoresDeAdjuntos
        abierto={selector}
        onCerrar={() => setSelector(null)}
        onAdjuntar={chat.agregarAdjunto}
        productos={entorno.productos}
        modelos={entorno.modelos}
        usuarioId={entorno.usuarioId}
        companyId={companyId}
        onModeloCreado={entorno.refrescarModelos}
      />
    </>
  );
};

export default ConversacionChatAtlas;
